import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdEmail } from "react-icons/md";
import { IoMdPerson } from "react-icons/io";
import { useAuth } from '../AuthContext';
import { userService } from '../services/api';
import { useGoogleMapsApi } from '../hooks/useGoogleMapsApi';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { Icon } from 'react-icons-kit';
import { eye } from 'react-icons-kit/feather/eye';
import { eyeOff } from 'react-icons-kit/feather/eyeOff';
import ReCAPTCHA from "react-google-recaptcha";



const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const isGoogleMapsLoaded = useGoogleMapsApi();
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('signupFormData');
    return savedData ? JSON.parse(savedData) : {
      // User data
      first_name: '',
      email: '',
      password: '',
      city: '',
      state: '',
      phone_number: '',
    };
  });

  const [locationInput, setLocationInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);


  useEffect(() => {
    // Save form data to localStorage whenever it changes
    localStorage.setItem('signupFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (isGoogleMapsLoaded) {
      initAutocomplete();
    }
  
    // Cleanup function
    return () => {
      const elements = [
        document.getElementById('location-input'),
        document.getElementById('location-input-mobile')
      ];
      
      elements.forEach(element => {
        if (element) {
          element.removeEventListener('keydown', () => {});
        }
      });
    };
  }, [isGoogleMapsLoaded]);
  
  const initAutocomplete = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      const elements = [
        document.getElementById('location-input'),
        document.getElementById('location-input-mobile')
      ];
  
      elements.forEach(element => {
        if (element) {  // Check if element exists
          const autocomplete = new window.google.maps.places.Autocomplete(
            element,
            {
              types: ['locality'],
              componentRestrictions: { country: 'us' },
              fields: ['address_components', 'geometry', 'name'],
            }
          );
  
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            handlePlaceSelect(place);
          });
  
          element.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const place = autocomplete.getPlace();
              if (place && place.address_components) {
                handlePlaceSelect(place);
              }
            }
          });
        }
      });
    } else {
      console.error('Google Maps Places API not loaded');
    }
  };

  const handlePlaceSelect = (place) => {
    if (place && place.address_components) {
      let city = '';
      let state = '';

      for (const component of place.address_components) {
        if (component.types.includes('locality')) {
          city = component.long_name;
        }
        if (component.types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
      }

      setLocationInput(`${city}, ${state}`);
      setFormData(prevState => ({
        ...prevState,
        city: city,
        state: state
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };


  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone_number) {
        setError('Phone number is required');
        return;
    }

    if (!captchaToken) {
        setError('Please complete the CAPTCHA verification');
        return;
    }

    try {
      setError('');
      // First check if phone number is already registered
      const checkData = {
        phone_number: formData.phone_number
      };
      await userService.checkPhoneNumber(checkData);
      
      // Phone number is valid, proceed with signup
      console.log('Submitting user signup data:', formData);
      
      const userData = {
        first_name: formData.first_name,
        email: formData.email,
        password: formData.password,
        city: formData.city,
        state: formData.state,
        phone_number: formData.phone_number,
        captcha_token: captchaToken
      };
      console.log('Sending user data to signup:', userData);
      const response = await userService.signup(userData);
      
      // Login the user and redirect to profile
      login(response.data);
      localStorage.removeItem('signupFormData');
      
      // Redirect to profile
      navigate('/profile');
    } catch (error) {
      console.error('Signup failed:', error);
      
      if (error.response?.data?.detail === 'Phone number already registered') {
        setError('This phone number is already registered.');
      } else if (error.response?.data?.detail === 'Invalid CAPTCHA') {
        setError('CAPTCHA verification failed. Please try again.');
        // Reset captcha
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      } else {
        setError('An error occurred during signup. Please try again.');
      }
    }
  };


  return (
    <div className="flex flex-col justify-center items-center min-h-screen p-8 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/leelo_login.jpg')" }}>
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10">
        <Link to="/" className="text-white hover:underline font-bold inline-block">
          &larr; Back to Home
        </Link>
      </div>
      <div className="bg-gradient-to-br from-white/95 to-uranian-blue/80 backdrop-blur-sm shadow-xl rounded-lg w-full max-w-md p-12 mt-16 sm:my-auto border border-white/20">
        <h1 className="mt-0 text-tropical-indigo">Join Leelo</h1>
        <p className="text-gray-700">Create your account.</p>
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Desktop version */}
          <div className="relative mb-4 hidden sm:block">
            <input 
              type="text" 
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Enter your first name" 
              required 
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
            <div className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray pointer-events-none">
              <IoMdPerson size={20} />
            </div>
          </div>
          <div className="relative mb-4 hidden sm:block">
            <input 
              id="location-input"
              type="text" 
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              placeholder="Enter your city and state" 
              required 
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
          </div>
          <div className="relative mb-4 hidden sm:block">
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email" 
              required 
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
            <div className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray pointer-events-none">
              <MdEmail size={20} />
            </div>
          </div>
          <div className="relative mb-4 hidden sm:block">
            <input 
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password" 
              required 
              className="w-full p-3 border border-white/30 rounded text-sm mb-4 pr-10 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showPassword ? eyeOff : eye} size={20} />
            </button>
          </div>
          <div className="relative mb-4 hidden sm:block">
            <PhoneInput
              country={'us'}
              value={formData.phone_number}
              onChange={phone => setFormData(prev => ({ ...prev, phone_number: phone }))}
              onlyCountries={['us', 'ca']}
              placeholder=""
              containerClass="w-full"
              inputClass="w-full p-3 border border-white/30 rounded text-sm mb-4 bg-white/70 focus:bg-white/90 focus:border-tropical-indigo/50 transition-all"
              inputStyle={{ width: '100%', height: '42px' }}
              containerStyle={{ width: '100%' }}
              required
            />
          </div>
          
          {/* Mobile version */}
          <div className="relative mb-4 sm:hidden">
            <input 
              type="text" 
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              placeholder="Enter your first name" 
              className="w-full p-3 border border-light-gray rounded text-sm mb-4 pr-10"
              required 
            />
            <div className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray pointer-events-none">
              <IoMdPerson size={20} />
            </div>
          </div>
          <div className="relative mb-4 sm:hidden">
            <input 
              id="location-input-mobile"
              type="text" 
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              placeholder="Enter your city & state"
              className="w-full p-3 border border-light-gray rounded text-sm mb-4"
              required 
            />
          </div>
          <div className="relative mb-4 sm:hidden">
            <input 
              type="email" 
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email" 
              className="w-full p-3 border border-light-gray rounded text-sm mb-4 pr-10"
              required 
            />
            <div className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray pointer-events-none">
              <MdEmail size={20} />
            </div>
          </div>
          <div className="relative mb-4 sm:hidden">
            <input 
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password" 
              className="w-full p-3 border border-light-gray rounded text-sm mb-4 pr-10"
              required 
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[40%] -translate-y-1/2 text-dark-gray bg-transparent border-none cursor-pointer"
            >
              <Icon icon={showPassword ? eyeOff : eye} size={20} />
            </button>
          </div>
          <div className="relative mb-4 sm:hidden">
            <PhoneInput
              country={'us'}
              value={formData.phone_number}
              onChange={phone => setFormData(prev => ({ ...prev, phone_number: phone }))}
              onlyCountries={['us', 'ca']}
              placeholder=""
              containerClass="w-full"
              inputClass="w-full p-3 border border-light-gray rounded text-sm mb-4"
              inputStyle={{ width: '100%', height: '42px' }}
              containerStyle={{ width: '100%' }}
              required
            />
          </div>
          
          {error && <div className="text-red-600 text-sm mt-2 p-2">{error}</div>}
          <p className="text-gray-600 text-xs leading-tight mt-1 mb-4">
            By signing up, you confirm your consent to receive text messages from Wenzi and you agree to our <Link to="/terms-of-use" className="text-tropical-indigo">Terms of Use</Link>.
          </p>

          <div className="mb-4 text-xs">
            <div className="inline-flex items-center">
              <input
                type="checkbox"
                id="remember-me"
                className="w-4 h-4 my-0"
              />
              <label htmlFor="remember-me" className="ml-2">Remember Me</label>
            </div>
          </div>
          
          <div className="mb-4 flex justify-center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
              onChange={handleCaptchaChange}
            />
          </div>
          
          <button 
            type="submit" 
            className="px-3 py-3 rounded font-bold text-base cursor-pointer transition-opacity bg-tropical-indigo text-white border border-tropical-indigo hover:opacity-90 w-full"
            disabled={!captchaToken}
          >
            Create Account
          </button>
        </form>
        <p className="mt-4 text-sm">Already have an account? <Link to="/login" className="text-tropical-indigo">Login</Link></p>
      </div>
    </div>
  );
};

export default Signup;
